// 3rd party libraries
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@material-ui/core';
import { PropTypes } from 'prop-types';

// Utils, constant
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import Logger from '@/common/utils/Logger';

// Component
import FormInputWithOnChange from '@/component/common/form/input-with-onchange';
import MarkdownView from './MarkdownView';
import { trI18n } from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

const readMarkdownFromUrl = async (url) => {
    if (!url) return '';
    try {
        new URL(url); // validate URL format
        const fetchResponse = await fetch(url);
        const text = await fetchResponse.text();
        return text || '';
    } catch (error) {
        Logger.error("Can't get markdown field data from S3: ", error);
        return '';
    }
};

export default function MarkdownFieldUI(props) {
    useTranslationSubscription();
    const defaultS3Url = props?.defaultValue || '';
    const publicForCustomer = props.data.structure.publicForCustomer;
    const isAddNewTicket = !props?.detailTicket;

    const isViewOnly =
        Number(publicForCustomer) === Constant.SETTING_FORM_DEFAULT.READ_ONLY ||
        props.viewFromProjectOfEscalateTicket ||
        !Utils.isEmptyString(props.detailTicket?.deletedAt) ||
        props.hasEditFieldPermissions === false;
    const [markdownText, setMarkdownText] = useState('');
    const [isViewMode, setIsViewMode] = useState(!isAddNewTicket);
    const [isLoading, setIsLoading] = useState(false);
    const [errMsg, setErrMsg] = useState('');

    // Sync error message from both react-hook-form errorlist and field.error (set by validateExceedSizeOfFieldTicket)
    useEffect(() => {
        let newMsg = '';
        if (props.errorlist && props.errorlist.hasOwnProperty(props?.name)) {
            newMsg = props.errorlist[props?.name].message;
        }
        if (!Utils.isEmptyObject(props.data?.error)) {
            newMsg = props.data?.error?.message;
        }
        setErrMsg(newMsg);
    }, [props.errorlist, props.data?.error]);

    // Keep a ref to the last saved/loaded markdown text for Cancel restore
    const savedMarkdownRef = useRef('');

    // --- Handlers ---

    const handleClickEdit = () => {
        if (isViewOnly) return;
        setIsViewMode(false);
    };

    const isValidMarkdownFileUrl = url => {
        try {
            if (!url || typeof url !== 'string') return false;
            const trimmed = url.trim();
            const validUrl = new URL(url);
            if (
                !trimmed ||
                /\s/.test(trimmed) ||
                validUrl?.origin !== process.env.REACT_APP_CLOUD_FRONT_URL
            )
                return false;
            return true;
        } catch {
            return false;
        }
    };

    const handleApply = () => {
        if (isViewOnly) return;
        setIsViewMode(true);
        savedMarkdownRef.current = markdownText;
    };

    const handleCancel = async () => {
        props?.clearErrors(props?.name);
        setErrMsg('');

        setIsLoading(true);
        const originalContent = await readMarkdownFromUrl(defaultS3Url);
        setMarkdownText(originalContent);
        savedMarkdownRef.current = originalContent;
        props?.setValue(props?.name, originalContent);
        setIsViewMode(true);
        setIsLoading(false);
    };

    const handleChange = (e) => {
        if (isViewOnly) return;
        if (props?.setIsEdited) {
            props?.setIsEdited(true);
            props?.setShowButtonEditTicket && props?.setShowButtonEditTicket();
            if (!props?.listFieldEdited?.includes(props?.name)) {
                props?.setListFieldEdited([...props.listFieldEdited, props?.name]);
            }
            if (props?.setOnChangeEdit) {
                props?.setOnChangeEdit(!props.onChangeEdit);
            }
        }
        const newValue = e.target.value;
        setMarkdownText(newValue);
        props?.setValue(props?.name, newValue);
    };

    // --- Load markdown from S3 URL ---

    const loadMarkdownFromUrl = async (url) => {
        setIsLoading(true);
        const text = await readMarkdownFromUrl(url);
        setMarkdownText(text);
        savedMarkdownRef.current = text;
        props?.setValue(props?.name, text);
        setIsLoading(false);
        return text;
    };

    // Init load / parentForm change (add new ticket, draft, edit knowledge)
    useEffect(() => {
        let isKnowledgeForm = [Constant.FORM_TYPE.FORM_KNOWLEDGE, Constant.FORM_TYPE.FORM_MASTER].includes(
            props?.parentForm?.type
        );
        if (isAddNewTicket || props?.isDraftMode || (props?.editTicket && !isKnowledgeForm)) {
            props.register(props?.name, { onChange: () => {} });
            if (isValidMarkdownFileUrl(defaultS3Url)) {
                loadMarkdownFromUrl(defaultS3Url);
            } else {
                // defaultValue is raw markdown content
                const content = defaultS3Url || '';
                setMarkdownText(content);
                savedMarkdownRef.current = content;
                props?.setValue(props?.name, content);
            }
        }
    }, [props?.parentForm]);

    // detailTicket change — reset view mode
    useEffect(() => {
        setIsViewMode(!isAddNewTicket);
    }, [props?.detailTicket]);

    // flagReset or displayMode → VIEW: reload from S3 and switch to view mode
    useEffect(() => {
        if (props?.flagReset || props?.displayMode === Constant.DISPLAY_MODE.VIEW) {
            props?.clearErrors(props?.name);
            setErrMsg('');
            const resetValue = props?.flagReset ? defaultS3Url : (props?.defaultValue || defaultS3Url);
            if (isValidMarkdownFileUrl(resetValue)) {
                loadMarkdownFromUrl(resetValue).then(() => {
                    setIsViewMode(true);
                    if (props?.flagReset) {
                        props.setFlagReset(false);
                    }
                });
            } else {
                const content = resetValue || '';
                setMarkdownText(content);
                savedMarkdownRef.current = content;
                props?.setValue(props?.name, content);
                setIsViewMode(true);
                if (props?.flagReset) {
                    props.setFlagReset(false);
                }
            }
        }
    }, [props?.flagReset, props?.displayMode]);

    // Tab switching: reload content for the newly selected tab
    useEffect(() => {
        if (!props?.tabId || !props?.defaultValue) return;
        if (isValidMarkdownFileUrl(props.defaultValue)) {
            loadMarkdownFromUrl(props.defaultValue);
        } else {
            // defaultValue is raw markdown content (restored from panelInitData.formData)
            setMarkdownText(props.defaultValue);
            savedMarkdownRef.current = props.defaultValue;
            props?.setValue(props?.name, props.defaultValue);
        }
    }, [props?.tabId, props?.defaultValue]);

    // --- Render ---

    const showViewBox = isViewOnly || isViewMode;

    return (
        <div className="form-addTicket field-" style={{ position: 'relative' }}>
            {/* Label */}
            <div className="cell-prod-status gap-8">
                {props?.isTooltipTitle ? (
                    <div className="d-flex">
                        <Tooltip title={props?.label}>
                            <span className="form-addTicket-label text-truncate">
                                <label
                                    className={`text-truncate line-height-18 w-f100 ${
                                        publicForCustomer ? 'form-addTicket-label-color' : 'form-addTicket-label'
                                    }`}
                                >
                                    {props?.label}
                                </label>
                            </span>
                        </Tooltip>
                        {props.data.structure.required && (
                            <span className="required padding-left-4">*</span>
                        )}
                    </div>
                ) : (
                    <label
                        className={`w-f100 ${
                            publicForCustomer ? 'form-addTicket-label-color' : 'form-addTicket-label'
                        }`}
                    >
                        {props?.label}{' '}
                        {props.data.structure.required && <span className="required">*</span>}
                    </label>
                )}

                {props?.isShowIconCopyFieldId && (
                    <Tooltip title={props?.data.id} placement="top" arrow>
                        <img
                            src="/assets/image/icon-copy.svg"
                            alt="copy"
                            onClick={() => navigator.clipboard.writeText(props?.data.id)}
                        />
                    </Tooltip>
                )}
            </div>

            {/* Field input area */}
            <div className="form-addTicket-input">
                {/* View Mode */}
                <Box style={{ display: showViewBox ? 'block' : 'none' }}>
                    <Box display="inline-flex" sx={{ width: '100%' }}>
                        {showViewBox && (
                            <MarkdownView
                                content={markdownText}
                                disabled={showViewBox}
                                hasError={!!errMsg}
                            />
                        )}
                        {!isViewOnly && (
                            <div>
                                <Button
                                    className="button-mermaid cursor-default"
                                    style={{ paddingRight: '0px' }}
                                    onClick={handleClickEdit}
                                    aria-label={trI18n('EDIT') || 'Edit'}
                                >
                                    <img
                                        src="/assets/image/button-url-edit.svg"
                                        alt=""
                                        className="cursor-pointer"
                                    />
                                </Button>
                            </div>
                        )}
                    </Box>
                </Box>

                {/* Edit Mode */}
                <Box style={{ display: !showViewBox ? 'block' : 'none' }}>
                    <Box display="flex" sx={{ width: '100%' }}>
                        <Box sx={{ flexGrow: 1, paddingRight: '10px' }}>
                            <FormInputWithOnChange
                                name={props?.name}
                                className="form-control form-control-wrapTextarea form-control-hg200"
                                variant="outlined"
                                defaultValue={markdownText}
                                value={markdownText}
                                multiline
                                disabled={isViewOnly}
                                errorlist={errMsg
                                    ? { ...props?.errorlist, [props?.name]: { message: errMsg } }
                                    : props?.errorlist
                                }
                                autoComplete="off"
                                required={props?.required}
                                onChange={handleChange}
                                data-cy="text-area-input-field"
                                isDisableOnBlur={props.isDisableOnBlur ? props.isDisableOnBlur : true}
                            />
                        </Box>
                        <Box display="inline-flex" alignItems="flex-start">
                            <Button
                                className="button-mermaid cursor-default"
                                onClick={handleApply}
                                aria-label={trI18n('APPLY') || 'Apply'}
                            >
                                <img
                                    src="/assets/image/button-url-apply.svg"
                                    alt=""
                                    className="cursor-pointer"
                                />
                            </Button>
                            <Button
                                className="button-mermaid cursor-default"
                                style={{ paddingRight: '0px' }}
                                onClick={handleCancel}
                                aria-label={trI18n('CANCEL') || 'Cancel'}
                            >
                                <img
                                    src="/assets/image/button-url-reset.svg"
                                    alt=""
                                    className="cursor-pointer"
                                />
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </div>

            {/* Error message — shown in view mode since FormInputWithOnChange handles it in edit mode */}
            {errMsg && showViewBox && (
                <p className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error" style={{ color: '#f44336' }}>{errMsg}</p>
            )}

            {/* Loading overlay */}
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        zIndex: 1
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </div>
    );
}

MarkdownFieldUI.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    defaultValue: PropTypes.string,
    data: PropTypes.any,
    detailTicket: PropTypes.any,
    parentForm: PropTypes.any,
    errorlist: PropTypes.any,
    flagReset: PropTypes.any,
    displayMode: PropTypes.any,
    tabId: PropTypes.any,
    isTooltipTitle: PropTypes.any,
    isShowIconCopyFieldId: PropTypes.any,
    hasEditFieldPermissions: PropTypes.any,
    viewFromProjectOfEscalateTicket: PropTypes.any,
    isDraftMode: PropTypes.any,
    editTicket: PropTypes.any,
    required: PropTypes.any,
    isDisableOnBlur: PropTypes.any,
    setIsEdited: PropTypes.any,
    setShowButtonEditTicket: PropTypes.any,
    setListFieldEdited: PropTypes.any,
    setOnChangeEdit: PropTypes.any,
    setFlagReset: PropTypes.any,
    listFieldEdited: PropTypes.any,
    onChangeEdit: PropTypes.any,
    setValue: PropTypes.any,
    clearErrors: PropTypes.any,
    register: PropTypes.any
};
